import { Fragment } from 'react'
import { useRouter } from '../../lib/router'
import { MarketingLayout } from './MarketingLayout'


const TICK = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--green-500, #22c55e)', flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const DASH = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: 'var(--neutral-400)', flexShrink: 0 }}>
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const SOON = (
  <span style={{
    fontSize: 11, fontWeight: 700, background: 'rgba(217,110,26,0.1)',
    color: 'var(--orange-500, #D96E1A)', padding: '2px 7px', borderRadius: 4,
    letterSpacing: '0.03em', whiteSpace: 'nowrap',
  }}>Soon</span>
)

const PLANS = [
  {
    name: 'Starter',
    price: 899,
    billing: '/month ex-VAT',
    tagline: 'One workshop, one owner-mechanic.',
    highlight: false,
    cta: 'Start 30-day pilot',
    features: [
      'Unlimited job cards',
      'WhatsApp intake & status updates',
      'Digital vehicle inspection (DVI)',
      'Quote & SARS-compliant invoice',
      'Customer & vehicle history',
      'PDF job cards & quotes',
      'Email & WhatsApp support',
    ],
  },
  {
    name: 'Workshop',
    price: 1899,
    billing: '/month ex-VAT',
    tagline: 'Growing workshops up to 5 bays.',
    highlight: true,
    badge: 'Most popular',
    cta: 'Start 30-day pilot',
    features: [
      'Unlimited job cards',
      'WhatsApp intake & status updates',
      'Digital vehicle inspection (DVI)',
      'Quote & SARS-compliant invoice',
      'Customer & vehicle history',
      'PDF job cards & quotes',
      'Priority email & WhatsApp support',
      'Multi-user roles (owner, foreman, office)',
      'Up to 5 bays on the live bay board',
      'Multiple WhatsApp numbers',
    ],
  },
  {
    name: 'Multi-site',
    price: 3499,
    billing: '/location/month ex-VAT',
    tagline: 'Two or more branches, one view.',
    highlight: false,
    comingSoon: true,
    cta: 'Coming soon',
    features: [
      'Everything in Workshop',
      'Dedicated onboarding & account manager',
      'Unlimited bays / users per site',
      'Multi-branch management',
    ],
  },
]

const COMPARISON_ROWS: {
  label: string; starter: boolean; workshop: boolean; multi: boolean;
  section?: string; comingSoon?: boolean; multiComingSoon?: boolean
}[] = [
  { label: 'Job cards', section: 'Core features', starter: true, workshop: true, multi: true },
  { label: 'WhatsApp intake & status updates', starter: true, workshop: true, multi: true },
  { label: 'Shared WhatsApp inbox', starter: true, workshop: true, multi: true },
  { label: 'Digital vehicle inspection (DVI)', starter: true, workshop: true, multi: true },
  { label: 'Quote builder', starter: true, workshop: true, multi: true },
  { label: 'SARS-compliant tax invoices', starter: true, workshop: true, multi: true },
  { label: 'Customer & vehicle history', starter: true, workshop: true, multi: true },
  { label: 'PDF exports', starter: true, workshop: true, multi: true },
  { label: 'Multi-user roles', section: 'Team', starter: false, workshop: true, multi: true },
  { label: 'Live bay board', starter: false, workshop: true, multi: true },
  { label: 'Multiple WhatsApp numbers', starter: false, workshop: true, multi: true },
  { label: 'Multi-branch management', section: 'Scale', starter: false, workshop: false, multi: true, multiComingSoon: true },
  { label: 'Dedicated account manager', starter: false, workshop: false, multi: true, multiComingSoon: true },
  { label: 'Custom onboarding', starter: false, workshop: false, multi: true, multiComingSoon: true },
  { label: 'Xero, Sage & Zoho accounting sync', section: 'Integrations', starter: false, workshop: true, multi: true },
  { label: 'PayFast and SnapScan payment workflows', starter: true, workshop: true, multi: true },
  { label: 'PayFast subscription billing', starter: true, workshop: true, multi: true },
  { label: 'Mobile DVI app', starter: true, workshop: true, multi: true },
  { label: 'WhatsApp broadcast campaigns', section: 'Coming soon', starter: false, workshop: false, multi: false, comingSoon: true },
  { label: 'Full offline-first local database', starter: false, workshop: false, multi: false, comingSoon: true },
  { label: 'Browser-cached load-shedding mode', starter: false, workshop: false, multi: false, comingSoon: true },
]

const PRICING_FAQS = [
  {
    q: 'Is there a per-mechanic seat fee?',
    a: 'No. Starter is capped at one user by design, but Workshop and Multi-site include unlimited users within your location. We never charge per mechanic.',
  },
  {
    q: 'What are the WhatsApp messaging costs?',
    a: "Meta charges separately for WhatsApp Business messaging based on the current WhatsApp pricing model and destination market. We keep this transparent and invoice it as actual usage once your number is connected.",
  },
  {
    q: 'Is the 30-day pilot really free, no credit card?',
    a: "Yes. We onboard you, import your customer data, and you run the system for 30 days. No card required. If it doesn't deliver, you owe nothing and we export your data in CSV within 48 hours.",
  },
  {
    q: 'Are prices VAT-inclusive?',
    a: 'All prices shown are exclusive of VAT. VAT at 15% is added at checkout. You receive a SARS-compliant tax invoice from JVS Ethics Lab, the company that owns and bills for AutoAxxiz.',
  },
  {
    q: 'Can I pay annually?',
    a: 'Yes — pay annually and receive a 15% discount, effectively getting 1.8 months free. Annual plans are invoiced upfront.',
  },
  {
    q: 'What payment methods do you accept for subscriptions?',
    a: 'EFT/bank transfer, debit order via DebiCheck, and card (Visa/Mastercard) via PayFast. All billing is in rand and appears from JVS Ethics Lab.',
  },
  {
    q: "What happens if I'm on Starter and my workshop grows?",
    a: 'Upgrade to Workshop at any time from Settings. The prorated difference is applied to your next invoice. No data migration, no downtime.',
  },
]

export function PricingPage() {

  const { navigate } = useRouter()

  return (
    <MarketingLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--navy-900, #0d1b2e)',
        padding: '80px var(--space-6) 64px',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{
            display: 'inline-block', background: 'rgba(217,110,26,0.15)',
            color: 'var(--orange-500, #D96E1A)', fontSize: 13, fontWeight: 600,
            padding: '4px 14px', borderRadius: 20, marginBottom: 24,
            border: '1px solid rgba(217,110,26,0.3)',
          }}>
            30-day free pilot · No credit card · Cancel any time
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.03em',
            lineHeight: 1.1, marginBottom: 20,
          }}>
            Straight-up pricing.<br />
            <span style={{ color: 'var(--orange-500, #D96E1A)' }}>In rand.</span>
          </h1>
          <p style={{
            fontSize: 18, color: 'var(--neutral-400)', lineHeight: 1.6, marginBottom: 0,
          }}>
            No hidden fees. No per-mechanic seat tax. Subscriptions are billed by JVS Ethics Lab, and WhatsApp usage remains visible as a separate pass-through cost.
          </p>
        </div>
      </section>

      {/* ── Pricing cards ─────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--bg-2, #f8f9fa)',
        padding: '0 var(--space-6)',
      }}>
        <div style={{
          maxWidth: 1080, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 24, transform: 'translateY(-40px)',
        }}>
          {PLANS.map(plan => (
            <div key={plan.name} style={{
              background: plan.highlight ? 'var(--navy-700, #1A2E4A)' : '#fff',
              border: plan.highlight ? '2px solid var(--orange-500, #D96E1A)' : '1px solid var(--border-subtle)',
              borderRadius: 16,
              padding: '32px 28px',
              position: 'relative',
              boxShadow: plan.highlight ? '0 20px 60px rgba(26,46,74,0.25)' : '0 2px 8px rgba(0,0,0,0.06)',
              opacity: plan.comingSoon ? 0.7 : 1,
            }}>
              {plan.badge && (
                <div style={{
                  position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                  background: 'var(--orange-500, #D96E1A)', color: '#fff',
                  fontSize: 12, fontWeight: 700, padding: '4px 16px', borderRadius: 20,
                  whiteSpace: 'nowrap',
                }}>
                  {plan.badge}
                </div>
              )}
              {plan.comingSoon && (
                <div style={{
                  position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                  background: 'var(--neutral-400, #9ca3af)', color: '#fff',
                  fontSize: 12, fontWeight: 700, padding: '4px 16px', borderRadius: 20,
                  whiteSpace: 'nowrap',
                }}>
                  Coming soon
                </div>
              )}
              <div style={{ marginBottom: 8 }}>
                <span style={{
                  fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                  color: plan.highlight ? 'var(--orange-400, #e8852e)' : 'var(--fg-3)',
                }}>{plan.name}</span>
              </div>
              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-end', gap: 4 }}>
                {plan.comingSoon ? (
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700, color: 'var(--fg-3)', letterSpacing: '-0.04em', lineHeight: 1 }}>R {plan.price.toLocaleString()}</span>
                ) : (
                  <span style={{
                    fontFamily: 'var(--font-display)', fontSize: 42, fontWeight: 700,
                    color: plan.highlight ? '#fff' : 'var(--navy-700)',
                    letterSpacing: '-0.04em', lineHeight: 1,
                  }}>R {plan.price.toLocaleString()}</span>
                )}
                <span style={{
                  fontSize: 14, color: plan.highlight ? 'rgba(255,255,255,0.55)' : 'var(--fg-3)',
                  marginBottom: 6,
                }}>{plan.billing}</span>
              </div>
              <p style={{
                fontSize: 14, color: plan.highlight ? 'rgba(255,255,255,0.7)' : 'var(--fg-3)',
                marginBottom: 28, lineHeight: 1.5,
              }}>{plan.tagline}</p>
              <button
                disabled={plan.comingSoon}
                onClick={() => !plan.comingSoon && navigate('/contact')}
                style={{
                  width: '100%', padding: '12px 0', borderRadius: 8,
                  fontSize: 15, fontWeight: 600,
                  cursor: plan.comingSoon ? 'not-allowed' : 'pointer',
                  background: plan.comingSoon ? 'var(--neutral-200, #e5e7eb)' : plan.highlight ? 'var(--orange-500, #D96E1A)' : 'transparent',
                  border: plan.highlight ? 'none' : '1px solid var(--border-strong)',
                  color: plan.comingSoon ? 'var(--fg-3)' : plan.highlight ? '#fff' : 'var(--navy-700)',
                  marginBottom: 28,
                }}
              >{plan.cta}</button>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {plan.features.map((f, i) => f ? (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {TICK}
                    <span style={{
                      fontSize: 14,
                      color: plan.highlight ? 'rgba(255,255,255,0.85)' : 'var(--fg-2)',
                    }}>{f}</span>
                  </li>
                ) : null)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── Annual note ───────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--bg-2, #f8f9fa)',
        padding: '0 var(--space-6) 72px',
        textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          background: '#fff', border: '1px solid var(--border-subtle)',
          borderRadius: 10, padding: '12px 24px',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--orange-500,#D96E1A)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          <span style={{ fontSize: 14, color: 'var(--fg-2)' }}>
            <strong>Save 15%</strong> when you pay annually — that's 1.8 months free. Annual plans invoiced upfront in ZAR.
          </span>
        </div>
      </section>

      {/* ── Feature comparison table ──────────────────────────────────────── */}
      <section style={{ padding: '80px var(--space-6)', background: '#fff' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4vw, 36px)',
            fontWeight: 700, color: 'var(--navy-700)', textAlign: 'center',
            letterSpacing: '-0.02em', marginBottom: 8,
          }}>Full feature comparison</h2>
          <p style={{ textAlign: 'center', color: 'var(--fg-3)', marginBottom: 48, fontSize: 15 }}>
            What is available now, and what is still planned.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '10px 16px', borderBottom: '2px solid var(--border-subtle)', color: 'var(--fg-3)', fontWeight: 500, width: '45%' }}>Feature</th>
                  {['Starter', 'Workshop', 'Multi-site'].map(h => (
                    <th key={h} style={{
                      textAlign: 'center', padding: '10px 16px',
                      borderBottom: '2px solid var(--border-subtle)',
                      color: h === 'Workshop' ? 'var(--orange-500,#D96E1A)' : 'var(--navy-700)',
                      fontWeight: 700, fontSize: 13,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <Fragment key={row.label}>
                    {row.section && (
                      <tr key={`section-${i}`}>
                        <td colSpan={4} style={{
                          padding: '20px 16px 8px',
                          fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.08em', color: 'var(--fg-4)',
                        }}>{row.section}</td>
                      </tr>
                    )}
                    <tr key={row.label} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 16px', color: 'var(--fg-2)' }}>{row.label}</td>
                      <td style={{ textAlign: 'center', padding: '12px 16px' }}>{row.comingSoon ? SOON : row.starter ? TICK : DASH}</td>
                      <td style={{ textAlign: 'center', padding: '12px 16px', background: 'rgba(217,110,26,0.04)' }}>{row.comingSoon ? SOON : row.workshop ? TICK : DASH}</td>
                      <td style={{ textAlign: 'center', padding: '12px 16px', opacity: 0.6 }}>{row.comingSoon || row.multiComingSoon ? SOON : row.multi ? TICK : DASH}</td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── WhatsApp cost explainer ────────────────────────────────────────── */}
      <section style={{ background: 'var(--bg-2, #f8f9fa)', padding: '72px var(--space-6)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'rgba(37,211,102,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#25D366" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700,
            color: 'var(--navy-700)', marginBottom: 16, letterSpacing: '-0.02em',
          }}>Honest about WhatsApp costs</h2>
          <p style={{ fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.7, marginBottom: 24 }}>
            WhatsApp Business messaging is billed separately by Meta based on message category, destination market and the current pricing model. AutoAxxiz keeps those charges visible instead of hiding them in your subscription.
          </p>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16, textAlign: 'left',
          }}>
            {[
              { label: 'Utility messages', rate: 'Usage based' },
              { label: 'Service conversations', rate: 'Usage based' },
              { label: 'Marketing campaigns', rate: 'Optional' },
            ].map(item => (
              <div key={item.label} style={{
                background: '#fff', border: '1px solid var(--border-subtle)',
                borderRadius: 10, padding: '16px 20px',
              }}>
                <div style={{ fontSize: 12, color: 'var(--fg-4)', marginBottom: 4 }}>{item.label}</div>
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700,
                  color: item.rate === 'Optional' ? '#22c55e' : 'var(--navy-700)',
                }}>{item.rate}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: 'var(--fg-4)', marginTop: 16 }}>
            Final WhatsApp charges are confirmed during Meta account setup and live billing validation.
          </p>
        </div>
      </section>

      {/* ── Pricing FAQ ───────────────────────────────────────────────────── */}
      <section style={{ padding: '80px var(--space-6)', background: '#fff' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 4vw, 34px)',
            fontWeight: 700, color: 'var(--navy-700)', textAlign: 'center',
            letterSpacing: '-0.02em', marginBottom: 48,
          }}>Pricing questions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {PRICING_FAQS.map((faq, i) => (
              <details key={i} style={{
                border: '1px solid var(--border-subtle)', borderRadius: 10,
                padding: '20px 24px', background: 'var(--bg-1)',
              }}>
                <summary style={{
                  cursor: 'pointer', fontWeight: 600, fontSize: 15,
                  color: 'var(--navy-700)', listStyle: 'none', userSelect: 'none',
                }}>
                  {faq.q}
                </summary>
                <p style={{
                  marginTop: 12, fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.7,
                }}>{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--navy-900, #0d1b2e)',
        padding: '80px var(--space-6)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4vw, 40px)',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.03em', marginBottom: 16,
          }}>Start your 30-day pilot today</h2>
          <p style={{ fontSize: 16, color: 'var(--neutral-400)', marginBottom: 36 }}>
            We'll onboard you, import your customer data, and have you running job cards on day one. No card required. No contract.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/contact')}
              style={{
                padding: '14px 32px', borderRadius: 8, fontSize: 16, fontWeight: 600,
                background: 'var(--orange-500, #D96E1A)', border: 'none',
                color: '#fff', cursor: 'pointer',
              }}
            >Claim your free pilot</button>
            <button
              onClick={() => navigate('/faq')}
              style={{
                padding: '14px 32px', borderRadius: 8, fontSize: 16, fontWeight: 500,
                background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.8)', cursor: 'pointer',
              }}
            >Read FAQ</button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  )
}
